// Desktop State Manager System (see engineering handoff, section 5).
// The shared, engineer-owned, non-visual source of truth for the Desktop
// and its app windows. Tracks open/closed, minimized/restored,
// focused/unfocused, x/y position, width/height, and stacking order -
// and receives actions from AppWindowButton / ShellAppWindow, making the
// result available to the Window Manager behavior baked into
// WindowManagerSurface and ShellAppWindow.
//
// Built with React Context + useReducer: no extra dependency, and this
// app only ever has a handful of windows open at once.
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import windowRegistry, { spaceRegistry } from "./windowRegistry";

function buildInitialWindows() {
  const windows = {};
  Object.values(windowRegistry).forEach((def, index) => {
    windows[def.id] = {
      id: def.id,
      isOpen: false,
      isMinimized: false,
      isFocused: false,
      x: def.defaultX ?? 60 + index * 40,
      y: def.defaultY ?? 60 + index * 40,
      width: def.defaultWidth ?? 420,
      height: def.defaultHeight ?? 480,
      zIndex: 1,
    };
  });
  return windows;
}

function initialSpace() {
  const firstKey = Object.keys(spaceRegistry)[0];
  return firstKey ?? null;
}

const initialState = {
  windows: buildInitialWindows(),
  zCounter: 1,
  currentSpace: initialSpace(),
};

// When a window opens/gains focus, every other window loses focus - only
// one window is ever "focused" at a time.
function unfocusOthers(windows, keepId) {
  const patch = {};
  Object.values(windows).forEach((win) => {
    if (win.id !== keepId && win.isFocused) {
      patch[win.id] = { ...win, isFocused: false };
    }
  });
  return patch;
}

// Build review NOV.01.2026: when the ACTIVE window is closed (or hidden), the
// top-most remaining visible window becomes active. Closing a window that
// isn't active leaves the active one as it is (nothing to hand over).
function handOverFocus(windows, closedId) {
  let next = null;
  Object.values(windows).forEach((w) => {
    if (w.id === closedId || !w.isOpen || w.isMinimized) return;
    if (!next || w.zIndex > next.zIndex) next = w;
  });
  return next ? { [next.id]: { ...next, isFocused: true } } : {};
}

function reducer(state, action) {
  const win = action.id ? state.windows[action.id] : undefined;

  switch (action.type) {
    case "OPEN_WINDOW": {
      if (!win) return state;
      const zIndex = state.zCounter + 1;
      return {
        ...state,
        zCounter: zIndex,
        windows: {
          ...state.windows,
          ...unfocusOthers(state.windows, action.id),
          [action.id]: {
            ...win,
            isOpen: true,
            isMinimized: false,
            isFocused: true,
            zIndex,
          },
        },
      };
    }
    case "CLOSE_WINDOW": {
      if (!win) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          ...(win.isFocused ? handOverFocus(state.windows, action.id) : {}),
          [action.id]: {
            ...win,
            isOpen: false,
            isMinimized: false,
            isFocused: false,
          },
        },
      };
    }
    case "FOCUS_WINDOW": {
      if (!win || !win.isOpen) return state;
      const zIndex = state.zCounter + 1;
      return {
        ...state,
        zCounter: zIndex,
        windows: {
          ...state.windows,
          ...unfocusOthers(state.windows, action.id),
          [action.id]: { ...win, isFocused: true, isMinimized: false, zIndex },
        },
      };
    }
    case "MINIMIZE_WINDOW": {
      if (!win) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          ...(win.isFocused ? handOverFocus(state.windows, action.id) : {}),
          [action.id]: { ...win, isMinimized: true, isFocused: false },
        },
      };
    }
    case "RESTORE_WINDOW": {
      if (!win) return state;
      const zIndex = state.zCounter + 1;
      return {
        ...state,
        zCounter: zIndex,
        windows: {
          ...state.windows,
          ...unfocusOthers(state.windows, action.id),
          [action.id]: { ...win, isMinimized: false, isFocused: true, zIndex },
        },
      };
    }
    case "MOVE_WINDOW": {
      if (!win) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          [action.id]: { ...win, x: action.x, y: action.y, userMoved: true },
        },
      };
    }
    case "RESIZE_WINDOW": {
      if (!win) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          [action.id]: {
            ...win,
            width: action.width,
            height: action.height,
            userResized: true,
          },
        },
      };
    }
    case "MAXIMIZE_WINDOW": {
      // action.rect = { x, y, width, height } computed from the surface.
      // The current geometry is remembered so un-maximizing restores it.
      if (!win) return state;
      const restore = win.isMaximized
        ? win.restoreRect
        : { x: win.x, y: win.y, width: win.width, height: win.height };
      return {
        ...state,
        windows: {
          ...state.windows,
          [action.id]: { ...win, ...action.rect, isMaximized: true, restoreRect: restore },
        },
      };
    }
    case "UNMAXIMIZE_WINDOW": {
      if (!win || !win.isMaximized) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          [action.id]: {
            ...win,
            ...(win.restoreRect || {}),
            isMaximized: false,
            restoreRect: undefined,
          },
        },
      };
    }
    case "SET_WINDOW_DEFAULTS": {
      // Per-instance default size/position (e.g. OP1WebAW props). Only fills
      // what the user hasn't changed yet in this session, so it never undoes
      // a drag or resize. Unknown ids are registered on the fly, so a window
      // works even without an entry in windowRegistry.js.
      const { defaults } = action;
      const base = win || {
        id: action.id,
        isOpen: false,
        isMinimized: false,
        isFocused: false,
        x: 60,
        y: 60,
        width: 420,
        height: 480,
        zIndex: 1,
      };
      const next = { ...base };
      const num = (v) => typeof v === "number" && Number.isFinite(v);
      if (!base.userMoved) {
        if (num(defaults.x)) next.x = defaults.x;
        if (num(defaults.y)) next.y = defaults.y;
      }
      if (!base.userResized) {
        if (num(defaults.width)) next.width = defaults.width;
        if (num(defaults.height)) next.height = defaults.height;
      }
      if (
        win &&
        next.x === win.x &&
        next.y === win.y &&
        next.width === win.width &&
        next.height === win.height
      ) {
        return state;
      }
      return { ...state, windows: { ...state.windows, [action.id]: next } };
    }
    case "SET_SPACE": {
      // Deliberately does not touch `windows` at all - switching
      // Background Space must never move/close/resize/reorder windows.
      return { ...state, currentSpace: action.id };
    }
    default:
      return state;
  }
}

const DesktopStateContext = createContext(undefined);

export function DesktopStateProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const openWindow = useCallback((id) => dispatch({ type: "OPEN_WINDOW", id }), []);
  const closeWindow = useCallback((id) => dispatch({ type: "CLOSE_WINDOW", id }), []);
  const focusWindow = useCallback((id) => dispatch({ type: "FOCUS_WINDOW", id }), []);
  const minimizeWindow = useCallback((id) => dispatch({ type: "MINIMIZE_WINDOW", id }), []);
  const restoreWindow = useCallback((id) => dispatch({ type: "RESTORE_WINDOW", id }), []);
  const moveWindow = useCallback(
    (id, x, y) => dispatch({ type: "MOVE_WINDOW", id, x, y }),
    []
  );
  const resizeWindow = useCallback(
    (id, width, height) => dispatch({ type: "RESIZE_WINDOW", id, width, height }),
    []
  );
  const setSpace = useCallback((id) => dispatch({ type: "SET_SPACE", id }), []);
  const maximizeWindow = useCallback(
    (id, rect) => dispatch({ type: "MAXIMIZE_WINDOW", id, rect }),
    []
  );
  const unmaximizeWindow = useCallback(
    (id) => dispatch({ type: "UNMAXIMIZE_WINDOW", id }),
    []
  );
  const setWindowDefaults = useCallback(
    (id, defaults) => dispatch({ type: "SET_WINDOW_DEFAULTS", id, defaults }),
    []
  );

  const actions = useMemo(
    () => ({
      openWindow,
      closeWindow,
      focusWindow,
      minimizeWindow,
      restoreWindow,
      moveWindow,
      resizeWindow,
      setSpace,
      setWindowDefaults,
      maximizeWindow,
      unmaximizeWindow,
    }),
    [
      setWindowDefaults,
      maximizeWindow,
      unmaximizeWindow,
      openWindow,
      closeWindow,
      focusWindow,
      minimizeWindow,
      restoreWindow,
      moveWindow,
      resizeWindow,
      setSpace,
    ]
  );

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <DesktopStateContext.Provider value={value}>
      {children}
    </DesktopStateContext.Provider>
  );
}

// Throws with a clear message instead of silently returning undefined -
// every code component that uses the Desktop state must be mounted
// somewhere under <DesktopStateProvider> (PlasmicHost.jsx and App.js
// both wrap their tree with it already).
export function useDesktopState() {
  const ctx = useContext(DesktopStateContext);
  if (!ctx) {
    throw new Error(
      "useDesktopState() was called outside a <DesktopStateProvider>. " +
        "Wrap the app root (or the Plasmic canvas host) with it."
    );
  }
  return ctx;
}

// Non-throwing variant for general-purpose components (e.g. ButtonPrimary)
// that only OPTIONALLY drive a window: returns undefined instead of throwing
// when there's no provider above, so they keep working anywhere.
export function useOptionalDesktopState() {
  return useContext(DesktopStateContext);
}

export function useWindowState(windowId) {
  const { state } = useDesktopState();
  return windowId ? state.windows[windowId] : undefined;
}
